// @ts-nocheck
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import Icon, { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import EditIcon from "@insite/mobius/Icons/Edit2";
import { DataTableCell } from "@insite/mobius/index";

import { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Close from "@store/Library/Components/Toolkit/Icons/Close";
import TwoButtonModal, { TwoButtonModalStyles } from "@store/Overrides/Components/TwoButtonModal";
import PromotionalTag from "@store/Library/Components/Common/PromotionalTag";
import Card, { CardStyles } from "@store/Library/Widgets/Card/Card";
import CardBody, { CardBodyStyles } from "@store/Library/Widgets/Card/CardBody";
import CardFooter, { CardFooterStyles } from "@store/Library/Widgets/Card/CardFooter";
import CardHeader, { CardHeaderStyles } from "@store/Library/Widgets/Card/CardHeader";
import { ManageDefaultPOTableRowProps } from "@store/Library/Widgets/ManageDefaultPO/ManageDefaultPOTableRow";
import Typography from "@store/MobiusOverrides/Typography/Typography";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React from "react";
import styled, { css } from "styled-components";

interface ManageDefaultPOCardStyles {
    card?: CardStyles;
    twoButtonModal?: TwoButtonModalStyles;
    label?: InjectableCss;
    cardHeader?: CardHeaderStyles;
    cardBody?: CardBodyStyles;
    clickable?: ClickableProps;
    cardFooter?: CardFooterStyles;
    confirmClickable?: ClickableProps;
    cardBodyColumn?: InjectableCss;
    editPoButton?: IconPresentationProps;
    removePoButton?: IconPresentationProps;
    informationTypography?: TypographyProps;
    tagWrapper?: InjectableCss;
}

const HorizontalRule = styled.hr`
    margin: 0.75rem 0;
    width: 100%;
    border: 1px solid rgba(74, 74, 74, 0.2);
`;
const Label = getStyledWrapper("label");
const manageDefaultPOTableRowStyles: ManageDefaultPOCardStyles = {
    tagWrapper: {
        css: css`
            position: absolute;
            top: -0.25rem;
            left: 80%;
        `,
    },
    twoButtonModal: {
        cancelButton: {
            css: css`
                margin-right: 0 !important;
            `,
        },
    },
    cardBodyColumn: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    informationTypography: {
        variant: "h5",
        css: css`
            margin-top: 0.25rem;
            text-align: center;
            font-size: ${pxToRem(16)};
        `,
    },
    cardBody: {
        css: css`
            display: flex;
            width: 100%;
            justify-content: space-around;
        `,
    },
    card: {
        css: css`
            border-radius: 0.75rem;
            width: 100%;
            border: 1.5px solid ${getColor("common.backgroundColorLightWarmGrey")};
        `,
    },
    label: {
        css: css`
            min-height: 1.375rem;
            display: inline-block;
            width: 100%;
            font-weight: 700;
            line-height: 1.375;
            font-size: ${pxToRem(14)};
        `,
    },
    clickable: {
        css: css`
            transition: opacity 0.4s ease-in-out;
            &:disabled {
                cursor: not-allowed;
                opacity: 0.4;
            }
        `,
    },
    removePoButton: {
        src: Close,
        size: 30,
        css: css`
            color: ${getColor("primary")};
            :hover {
                color: ${getColor("text.linkHover")};
            }
        `,
    },
    cardFooter: {
        css: css`
            padding-inline: 0;
            display: flex;
            width: 100%;
            align-items: center;
        `,
    },
    editPoButton: {
        src: EditIcon,
        size: 30,
        css: css`
            color: ${getColor("primary")};
            :hover {
                color: ${getColor("text.linkHover")};
            }
        `,
    },
    cardHeader: {
        css: css`
            position: relative;
            width: 100%;
            text-align: center;
        `,
    },
};
export interface ManageDefaultPOCardProps
    extends Omit<ManageDefaultPOTableRowProps, "extendedStyles" | "overrideStyles"> {
    extendedStyles?: ManageDefaultPOCardStyles;
    overrideStyles?: ManageDefaultPOCardStyles;
}

const ManageDefaultPOCard: React.FC<ManageDefaultPOCardProps> = ({
    customerPoNumber,
    extendedStyles,
    overrideStyles,
    setEditingPo,
    isEditing,
    onDelete,
    isActive,
}) => {
    const poNumber = customerPoNumber.poNumber;
    const startDate = new Date(customerPoNumber.startDate);
    const endDate = customerPoNumber.endDate ? new Date(customerPoNumber.endDate) : undefined;
    const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
    const styles = useExpandProps(manageDefaultPOTableRowStyles, extendedStyles, overrideStyles);
    return (
        <Card size="medium" align="flex-start" justify="center" extendedStyles={styles.card}>
            <CardHeader extendedStyles={styles.cardHeader}>
                <Label {...styles.label}>{translate("PO Number")}</Label>
                <Typography {...styles.informationTypography}>{poNumber}</Typography>
                {isActive && (
                    <StyledWrapper {...styles.tagWrapper}>
                        <PromotionalTag variant="active" tag="active" />
                    </StyledWrapper>
                )}
            </CardHeader>
            <CardBody extendedStyles={styles.cardBody}>
                <StyledWrapper {...styles.cardBodyColumn}>
                    <Label {...styles.label}>{translate("Start Date")}</Label>
                    <Typography {...styles.informationTypography}>{startDate.toLocaleDateString()}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.cardBodyColumn}>
                    <Label {...styles.label}>{translate("End Date")}</Label>
                    <Typography {...styles.informationTypography}>
                        {endDate ? endDate.toLocaleDateString() : ""}
                    </Typography>
                </StyledWrapper>
            </CardBody>
            <HorizontalRule />
            <CardFooter justifyContent="space-around" extendedStyles={styles.cardFooter}>
                <Clickable
                    disabled={isEditing}
                    onClick={() => setEditingPo(customerPoNumber)}
                    data-test-selector="PoOverride_editPoButton"
                    {...styles.clickable}
                >
                    <Icon {...styles.editPoButton} />
                    <VisuallyHidden>{translate("edit this product")}</VisuallyHidden>
                </Clickable>
                <Clickable
                    disabled={isEditing}
                    onClick={() => setIsConfirmModalOpen(true)}
                    data-test-selector="PoOverride_removePoButton"
                    {...styles.clickable}
                >
                    <IconMemo {...styles.removePoButton} />
                    <VisuallyHidden>{translate("Removed this product")}</VisuallyHidden>
                </Clickable>

                {/* {isActive && (
                    <StyledWrapper {...styles.floatingTagWrapper}>
                        <PromotionalTag variant="active" tag="active" />
                    </StyledWrapper>
                )} */}
            </CardFooter>
            <TwoButtonModal
                // eslint-disable-next-line spire/avoid-dynamic-translate
                extendedStyles={styles.twoButtonModal}
                messageText={translate("Are you sure you want to delete this default PO?")}
                submitTestSelector="PoOverride_confirmDeleteButton"
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Confirm")}
                headlineText={translate("Delete PO")}
                modalIsOpen={isConfirmModalOpen}
                onCancel={() => setIsConfirmModalOpen(false)}
                onSubmit={() => onDelete(customerPoNumber.id)}
            />
        </Card>
    );
};

export default ManageDefaultPOCard;
